import { z } from 'zod';
import { apiClient } from '@/lib/http/client';
import type { ApiResponse } from '@/lib/http/types';

/**
 * `GET /api/media/url/:key` double l'enveloppe `datas` : le retour brut de ms-medias-s3
 * (`{ statusCode, datas: <url> }`) est lui-même posé sous le `datas` du gateway, donc l'URL
 * présignée vit à `data.datas.datas` — pas de champ `url`. Confirmé sur un exemple réel
 * (2026-09-17) ; point déjà signalé côté SAAS-1069 comme "à vérifier avec SAAS-1025". Si le
 * back aplatit un jour cette enveloppe, ce schema doit casser au parse plutôt que de laisser
 * une image silencieusement cassée.
 */
const MediaUrlResponseSchema = z.object({ datas: z.string() });

/**
 * Fetches a pre-signed S3 URL for a variant document stored in MinIO.
 * The URL can then be used with plain `fetch()` to retrieve the file content.
 *
 * `mediaKey` is the full S3 key — `Document.filename` since SAAS-1084, or the key
 * carried by a `media:` marker.
 */
export const fetchVariantDocumentUrl = async (mediaKey: string): Promise<string> => {
  const { data } = await apiClient.get<ApiResponse<unknown>>(`/api/media/url/${mediaKey}`);
  return MediaUrlResponseSchema.parse(data.datas).datas;
};

const UUID_FROM_FILENAME_RE =
  /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\.[^.]+$/i;

/**
 * Repli hérité : reconstruit la clé S3 d'un document `file` de variante à partir de
 * `variantKey` + l'UUID présent dans `originalName` (`<uuid>.<ext>`), pour les documents
 * créés AVANT SAAS-1084.
 *
 * ⚠️ Ce n'est plus le chemin normal. Depuis SAAS-1084, la clé S3 est persistée dans
 * `Document.filename` et fixée par le serveur (`POST /api/media/upload`) : c'est elle
 * qu'il faut lire, ce repli n'intervient que lorsqu'elle est absente.
 *
 * ⚠️ Il ne rattrape pas tout — il n'y a pas d'UUID à extraire d'un `originalName` humain
 * (« Note de cadrage-1.pdf »), et il suppose le préfixe `variants/<variantKey>/`, que les
 * objets déposés hors de cette route ne portent pas forcément. À supprimer une fois le
 * rattrapage des documents existants fait (ticket de suite de SAAS-1084).
 *
 * Partagé entre `admin` (DocumentPreviewModal) et `tech-tests` (ResourcesSection).
 */
export function deriveMediaKeyFallback(
  variantKey: string | undefined,
  originalName: string,
): string | null {
  if (!variantKey) return null;
  const match = UUID_FROM_FILENAME_RE.exec(originalName);
  if (!match) return null;
  return `variants/${variantKey}/${match[1]}`;
}
